import { useEffect, useMemo, useState } from "react";
import type { HdcReport } from "../../../../ExperianPrivateResponse.types";
import { TabId, getTabs, isEmptyReport, mapProductResult } from "./mappers";
import { C, font } from "./theme";
import { HdcTabList, panelId, tabId } from "./HdcTabList";
import { HdcEmptyState } from "./HdcEmptyState";
import { ResumenTab } from "./tabs/ResumenTab";
import { ObligacionesTab } from "./tabs/ObligacionesTab";
import { TarjetasTab } from "./tabs/TarjetasTab";
import { CuentasConsultasTab } from "./tabs/CuentasConsultasTab";
import { EndeudamientoGlobalTab } from "./tabs/EndeudamientoGlobalTab";
import { EvolucionTab } from "./tabs/EvolucionTab";
import { MicrocreditoTab } from "./tabs/MicrocreditoTab";

export const HdcReportView = ({
  report,
  executionDatetime,
  idPrefix,
}: {
  report: HdcReport | null | undefined;
  executionDatetime?: string | null;
  /** Unique per step, used for tab/panel ids. */
  idPrefix: string;
}) => {
  const tabs = useMemo(() => getTabs(report), [report]);
  const [active, setActive] = useState<TabId>("resumen");

  // Fall back to the first tab if the active one disappears (new report).
  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.id === active)) setActive(tabs[0].id);
  }, [tabs, active]);

  const wrapper = { ...font, color: C.white, width: "100%", boxSizing: "border-box" as const };

  if (!report || isEmptyReport(report)) {
    return (
      <div style={{ ...wrapper, padding: "8px 24px 28px" }}>
        <HdcEmptyState product={mapProductResult(report, executionDatetime)} />
      </div>
    );
  }

  const renderPanel = () => {
    switch (active) {
      case "resumen":
        return <ResumenTab report={report} executionDatetime={executionDatetime} />;
      case "obligaciones":
        return <ObligacionesTab report={report} />;
      case "tarjetas":
        return <TarjetasTab report={report} />;
      case "cuentas":
        return <CuentasConsultasTab report={report} />;
      case "global":
        return <EndeudamientoGlobalTab report={report} />;
      case "evolucion":
        return <EvolucionTab report={report} />;
      case "microcredito":
        return <MicrocreditoTab report={report} />;
    }
  };

  return (
    <div style={{ ...wrapper, display: "flex", flexWrap: "wrap", gap: 20 }}>
      <HdcTabList tabs={tabs} active={active} onChange={setActive} idPrefix={idPrefix} />
      <div
        role="tabpanel"
        id={panelId(idPrefix, active)}
        aria-labelledby={tabId(idPrefix, active)}
        tabIndex={0}
        style={{
          flex: "999 1 560px",
          minWidth: 0,
          padding: "8px 24px 32px",
          boxSizing: "border-box",
          outline: "none",
        }}
      >
        {renderPanel()}
      </div>
    </div>
  );
};
